import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Tool } from '../models/tool';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ToolService {
  private readonly apiUrl = `${environment.apiUrl}/api/tools`;
  private readonly toolsSubject = new BehaviorSubject<Tool[]>([]);
  readonly tools$ = this.toolsSubject.asObservable();

  constructor(private http: HttpClient) {}

  getTools(forceRefresh = false): Observable<Tool[]> {
    if (!forceRefresh && this.toolsSubject.value.length > 0) {
      return this.tools$;
    }

    return this.http.get<Tool[]>(this.apiUrl).pipe(
      tap((tools) => this.toolsSubject.next(tools)),
    );
  }

  createTool(tool: Omit<Tool, 'id'>): Observable<Tool> {
    return this.http.post<Tool>(this.apiUrl, tool).pipe(
      tap((createdTool) => {
        this.toolsSubject.next([...this.toolsSubject.value, createdTool]);
      }),
    );
  }

  updateTool(id: number, tool: Partial<Omit<Tool, 'id'>>): Observable<Tool> {
    return this.http.put<Tool>(`${this.apiUrl}/${id}`, tool).pipe(
      tap((updatedTool) => {
        this.toolsSubject.next(
          this.toolsSubject.value.map((existingTool) =>
            existingTool.id === id ? updatedTool : existingTool,
          ),
        );
      }),
    );
  }

  deleteTool(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        this.toolsSubject.next(
          this.toolsSubject.value.filter((tool) => tool.id !== id),
        );
      }),
    );
  }
}